import { siteApi } from "@api/index";
import { openMegaShareFriendDialog } from "@components/features/MegaShare/MegaShareFriendDialog";
import { toast } from "@components/utils/Toaster";
import { SEON_FEATURE } from "@configs/seon";
import { DEFAULT_ERROR_MESSAGE } from "@constants/misc";
import { useWSMegaShareUpdated } from "@hooks/useAppWS";
import { AppDispatch } from "@stores";
import preferenceSlice from "@stores/preference/slices";
import { resolveAuthResponseError } from "@utils/error-resolvers";
import { MegaShareState } from "@views/PromotionMegaSharePage/types";
import { MegaShareActor } from "@views/PromotionMegaSharePage/types/mega-share-state";
import {
  APIErrorCodes,
  ErrorResponse,
  MegaShareStatus,
  MegaShareTxModel,
  MegaShareTxStatus,
  QueryError,
  WalletTxMethod,
  MegaShareFormTargetType,
} from "kz-ui-sdk";
import moment from "moment";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import usePreference from "./usePreference";
import { useSeonSession } from "./useSeonSession";
import useUserMode from "./useUserMode";

interface ShareBonusParams {
  targetType: MegaShareFormTargetType;
  targetValue: string;
}

const CAN_GIVER_SHOW_EXPIRED_STATE = [MegaShareState.PENDING, MegaShareState.LOCKED];
const CAN_TAKER_SHOW_EXPIRED_STATE = [MegaShareState.CLAIMABLE, MegaShareState.PENDING, MegaShareState.LOCKED];

function getGiverState(tx: MegaShareStatus["megaShareTx"] | undefined): MegaShareState {
  if (!tx) return MegaShareState.UNAVAILABLE;

  // Check expiration first (even if status says something else, if expired, it's expired)
  const now = moment();
  if (tx.expiredAt && moment(tx.expiredAt).isBefore(now)) {
    return MegaShareState.EXPIRED;
  }

  // Map MegaShareTxStatus to MegaShareState
  switch (tx.status) {
    case MegaShareTxStatus.Expired:
      return MegaShareState.EXPIRED;
    case MegaShareTxStatus.Completed:
    case MegaShareTxStatus.FriendClaimed:
      return MegaShareState.CLAIMABLE;
    case MegaShareTxStatus.Shared:
      return MegaShareState.PENDING;
    case MegaShareTxStatus.Issued:
    case MegaShareTxStatus.BonusUnlocked:
      return MegaShareState.LOCKED;
    default:
      return MegaShareState.UNAVAILABLE;
  }
}

interface ShareBonusOptions {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

interface UseMegaShareStatusResult {
  shouldShowGiverMegaShare: boolean;
  shouldShowTakerMegaShare: boolean;
  giver?: {
    status: MegaShareState;
    takerInfo?: {
      type: MegaShareFormTargetType;
      value: string;
    };
  };
  taker?: {
    status: MegaShareState;
    giverInfo?: {
      type: MegaShareFormTargetType;
      value: string;
    };
  };
  isAvailable: boolean;
  isFetching: boolean;
  details?: {
    instruction: {
      en: string;
      text: string;
    };
    remainingSeconds: number;
    claimAmount: number;
    takerClaimAmount: number;
    targetTypes: MegaShareFormTargetType[];
  };
  defaultSharingMethod?: MegaShareFormTargetType;
  refetch: () => void;
  openFriendDialog: () => void;
  claimReward: (actor: MegaShareActor) => Promise<boolean>;
  shareBonus: (params: ShareBonusParams, options?: ShareBonusOptions) => Promise<void>;
  isClaiming: boolean;
  isSharing: boolean;
  isGiverMegaShareUnseen: boolean;
  markGiverMegaShareAsSeen: () => void;
}

function getTakerState(tx: MegaShareStatus["sharedMegaShareTx"] | undefined): MegaShareState {
  if (!tx) return MegaShareState.UNAVAILABLE;

  // Check expiration first
  const now = moment();
  if (tx.expiredAt && moment(tx.expiredAt).isBefore(now)) {
    return MegaShareState.EXPIRED;
  }

  // Map MegaShareTxStatus to MegaShareState for taker
  switch (tx.status) {
    case MegaShareTxStatus.Expired:
      return MegaShareState.EXPIRED;
    case MegaShareTxStatus.Shared:
    case MegaShareTxStatus.FriendClaimed:
    case MegaShareTxStatus.Completed:
      return MegaShareState.CLAIMABLE;
    default:
      return MegaShareState.UNAVAILABLE;
  }
}

function mapSharingMethodToTargetTypes(sharingMethod?: string[]): MegaShareFormTargetType[] {
  if (!sharingMethod || sharingMethod.length === 0) {
    return [MegaShareFormTargetType.PHONE_NUMBER];
  }

  const methodMap: Record<string, MegaShareFormTargetType> = {
    mobile: MegaShareFormTargetType.PHONE_NUMBER,
    phone: MegaShareFormTargetType.PHONE_NUMBER,
    email: MegaShareFormTargetType.EMAIL,
    username: MegaShareFormTargetType.USERNAME,
  };

  const targetTypes: MegaShareFormTargetType[] = [];
  for (const method of sharingMethod) {
    const normalizedMethod = method.toLowerCase().trim();
    const targetType = methodMap[normalizedMethod];
    if (targetType && !targetTypes.includes(targetType)) {
      targetTypes.push(targetType);
    }
  }

  return targetTypes.length > 0 ? targetTypes : [MegaShareFormTargetType.PHONE_NUMBER];
}

function mapSharingMethodToTargetType(method?: string): MegaShareFormTargetType | undefined {
  if (!method) return undefined;

  const normalizedMethod = method.toLowerCase().trim();
  const methodMap: Record<string, MegaShareFormTargetType> = {
    mobile: MegaShareFormTargetType.PHONE_NUMBER,
    phone: MegaShareFormTargetType.PHONE_NUMBER,
    email: MegaShareFormTargetType.EMAIL,
    username: MegaShareFormTargetType.USERNAME,
  };

  return methodMap[normalizedMethod];
}

function calculateRemainingSeconds(
  expiredAt: moment.Moment | null | undefined,
  createdAt: moment.Moment | null | undefined,
  expiryHours?: number,
): number {
  // If expiredAt is provided, use it directly
  if (expiredAt) {
    const diff = moment(expiredAt).diff(moment(), "seconds");
    return Math.max(0, diff);
  }

  // If expiredAt is not available, calculate from createdAt + expiryHours
  if (createdAt && expiryHours !== undefined && expiryHours > 0) {
    const calculatedExpiredAt = moment(createdAt).add(expiryHours, "hours");
    const diff = calculatedExpiredAt.diff(moment(), "seconds");
    return Math.max(0, diff);
  }

  return 0;
}

export function useMegaShareStatus(options?: { disableWsRefetch?: boolean }): UseMegaShareStatusResult {
  const { isGuest } = useUserMode();
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const { seenGiverMegaShareTxId } = usePreference();
  const [isClaiming, setIsClaiming] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  const {
    data: rewardStatus,
    isLoading: isFetchingRewardStatus,
    refetch: refetchRewardStatus,
  } = siteApi.useGetRewardsStatusQuery(undefined, {
    skip: isGuest,
  });

  const [shareMegaShareMutation] = siteApi.useShareMegaShareMutation();
  const [claimRewardMutation] = siteApi.useClaimRewardMutation();
  const { getSession: getSeonSession } = useSeonSession(SEON_FEATURE.PROMOTION_MEGA_SHARE);

  useWSMegaShareUpdated({
    cb: () => {
      if (options?.disableWsRefetch) return;
      refetchRewardStatus();
    },
  });

  const markGiverMegaShareAsSeen = useCallback(() => {
    const megaShareStatus = rewardStatus?.[WalletTxMethod.MegaShare] as MegaShareStatus | undefined;
    const currentTxId = megaShareStatus?.megaShareTx?.id;

    if (currentTxId) {
      const txIdString = String(currentTxId);
      dispatch(preferenceSlice.actions.setSeenGiverMegaShareTxId(txIdString));
    }
  }, [rewardStatus, dispatch]);

  const handleShareMegaShare = useCallback(
    async (method: MegaShareFormTargetType, value: string) => {
      await shareMegaShareMutation({ method, value: (value || "")?.toLowerCase()?.trim() }).unwrap();
      refetchRewardStatus();
    },
    [shareMegaShareMutation, refetchRewardStatus],
  );

  const handleOpenFriendDialog = useCallback(() => {
    openMegaShareFriendDialog();
  }, []);

  const handleClaimReward = useCallback(
    async (actor: MegaShareActor) => {
      setIsClaiming(true);
      try {
        let isSuccess = true;
        const seonSession = await getSeonSession();
        const walletTxMethod = actor === MegaShareActor.SHARER ? WalletTxMethod.MegaSharer : WalletTxMethod.MegaSharee;
        const claimRewardRes = await claimRewardMutation({
          seonSession,
          query: { type: walletTxMethod },
        });

        if ("error" in claimRewardRes) {
          isSuccess = false;
          const error = claimRewardRes.error as QueryError<ErrorResponse>;
          const errorMessage = error?.data?.error?.message;

          // Check if bonus is already claimed or not claimable
          if (errorMessage === APIErrorCodes.ClaimReward.AlreadyClaimed) {
            toast.warning(t("bonus already claimed"), {
              id: "mega-share-claimed-toast",
            });
          } else if (errorMessage === APIErrorCodes.MegaShare.NotClaimable) {
            toast.warning(t("sorry this bonus is no longer available"), {
              id: "mega-share-unavailable-toast",
            });
          } else {
            toast.error(resolveAuthResponseError(error, t));
          }
        }
        // return success response
        return isSuccess;
      } finally {
        refetchRewardStatus();
        setIsClaiming(false);
      }
    },
    [claimRewardMutation, getSeonSession, refetchRewardStatus, t],
  );

  const handleShareBonus = useCallback(
    async ({ targetType, targetValue }: ShareBonusParams, options?: ShareBonusOptions) => {
      setIsSharing(true);
      try {
        await handleShareMegaShare(targetType, targetValue);
        options?.onSuccess?.();
      } catch (error) {
        const errorMessage = (error as QueryError<ErrorResponse>)?.data?.error?.message;
        if (errorMessage) {
          options?.onError?.(errorMessage);
        } else {
          options?.onError?.(DEFAULT_ERROR_MESSAGE);
        }
        throw error;
      } finally {
        setIsSharing(false);
      }
    },
    [handleShareMegaShare],
  );

  const {
    shouldShowGiverMegaShare,
    shouldShowTakerMegaShare,
    isGiverMegaShareUnseen,
    giver,
    taker,
    isAvailable,
    details,
  } = useMemo(() => {
    const megaShareStatus = rewardStatus?.[WalletTxMethod.MegaShare] as MegaShareStatus | undefined;
    const enabled = megaShareStatus?.enabled ?? false;
    const megaShareTx = megaShareStatus?.megaShareTx;
    const sharedMegaShareTx = megaShareStatus?.sharedMegaShareTx;

    let giverState = getGiverState(megaShareTx);
    let takerState = getTakerState(sharedMegaShareTx);

    // Extract takerInfo from giver's transaction using sharingInfo
    // Note: takerInfo is only needed when status is PENDING or CLAIMABLE (after sharing)
    // For LOCKED status, no takerInfo is needed (adapter will handle UNAVAILABLE correctly)
    const takerInfo =
      giverState !== MegaShareState.LOCKED &&
      giverState !== MegaShareState.UNAVAILABLE &&
      megaShareTx?.sharingInfo?.value
        ? {
            value: megaShareTx.sharingInfo.value,
            type: mapSharingMethodToTargetType(megaShareTx.sharingInfo.method) || MegaShareFormTargetType.PHONE_NUMBER,
          }
        : undefined;

    // Extract giverInfo from shared transaction using sharingInfo
    // Note: giverInfo is needed for taker when they have a shared transaction
    const giverInfo =
      takerState !== MegaShareState.UNAVAILABLE && sharedMegaShareTx?.sharingInfo?.value
        ? {
            value: sharedMegaShareTx.sharingInfo.value,
            type:
              mapSharingMethodToTargetType(sharedMegaShareTx.sharingInfo.method) ||
              MegaShareFormTargetType.PHONE_NUMBER,
          }
        : undefined;

    // Get earningSettings from SDK
    const earningSettings = megaShareStatus?.earningSettings;

    // Calculate remaining seconds first to check expiration
    // For giver: use megaShareTx (prioritize sharedAt after sharing)
    // For taker: use sharedMegaShareTx
    const remainingSeconds = megaShareTx
      ? calculateRemainingSeconds(
          megaShareTx.expiredAt,
          megaShareTx.unlockedAt || megaShareTx.createdAt,
          earningSettings?.expiryHours,
        )
      : sharedMegaShareTx
        ? calculateRemainingSeconds(
            sharedMegaShareTx.expiredAt,
            sharedMegaShareTx.unlockedAt || sharedMegaShareTx.createdAt,
            earningSettings?.expiryHours,
          )
        : 0;

    // Override state to EXPIRED if remainingSeconds is 0 or less
    // This handles cases where backend hasn't updated status yet
    if (remainingSeconds <= 0) {
      if (CAN_GIVER_SHOW_EXPIRED_STATE.includes(giverState) && megaShareTx) {
        giverState = MegaShareState.EXPIRED;
      }
      if (CAN_TAKER_SHOW_EXPIRED_STATE.includes(takerState) && sharedMegaShareTx) {
        takerState = MegaShareState.EXPIRED;
      }
    }

    // Get sharer claim amount from the transaction
    const sharerClaimAmount = getClaimAmount(megaShareTx);
    const takerClaimAmount = getClaimAmount(sharedMegaShareTx);
    // Map sharingMethod from SDK to targetTypes
    const targetTypes = mapSharingMethodToTargetTypes(megaShareStatus?.sharingMethod as string[] | undefined);

    // Get instruction text - use lockedInstructions when state is LOCKED
    const currentLang = i18n.language || "en";
    const lockedInstructions = megaShareStatus?.lockedInstructions as { [langCode: string]: string } | undefined;
    const lockedInstructionText =
      giverState === MegaShareState.LOCKED && lockedInstructions
        ? lockedInstructions[currentLang] || lockedInstructions["en"] || ""
        : "";

    const giverTxId = megaShareTx?.id;
    const isGiverMegaShareUnseen = enabled && !!giverTxId && giverTxId !== seenGiverMegaShareTxId;

    return {
      shouldShowGiverMegaShare: enabled && !!megaShareTx,
      shouldShowTakerMegaShare: enabled && !!sharedMegaShareTx && takerState === MegaShareState.CLAIMABLE,
      isGiverMegaShareUnseen,
      giver: {
        status: giverState,
        ...(takerInfo && { takerInfo }),
      },
      taker: {
        status: takerState,
        ...(giverInfo && { giverInfo }),
      },
      isAvailable: enabled,
      details: {
        instruction: {
          en: lockedInstructionText,
          text: lockedInstructionText,
        },
        remainingSeconds,
        claimAmount: sharerClaimAmount,
        takerClaimAmount: takerClaimAmount,
        targetTypes,
      },
    };
  }, [rewardStatus, seenGiverMegaShareTxId, i18n.language]);

  if (isGuest) {
    return {
      shouldShowGiverMegaShare: false,
      shouldShowTakerMegaShare: false,
      giver: undefined,
      taker: undefined,
      isAvailable: false,
      isFetching: false,
      details: undefined,
      defaultSharingMethod: undefined,
      refetch: () => {},
      openFriendDialog: () => {},
      claimReward: async () => false,
      isClaiming: false,
      isSharing: false,
      shareBonus: async () => {},
      isGiverMegaShareUnseen: false,
      markGiverMegaShareAsSeen: () => {},
    };
  }

  return {
    shouldShowGiverMegaShare,
    shouldShowTakerMegaShare,
    giver,
    taker,
    isAvailable,
    isFetching: isFetchingRewardStatus,
    details,
    defaultSharingMethod: rewardStatus?.[WalletTxMethod.MegaShare]?.defaultSharingMethod,
    refetch: refetchRewardStatus,
    openFriendDialog: handleOpenFriendDialog,
    claimReward: handleClaimReward,
    isClaiming,
    isSharing,
    shareBonus: handleShareBonus,
    isGiverMegaShareUnseen,
    markGiverMegaShareAsSeen,
  };
}

/**
 * Get claim amount from tx
 * Final amount is half of the total amount
 */
const getClaimAmount = (megaShareTx: MegaShareTxModel | null | undefined): number => {
  const claimAmount = megaShareTx?.amount
    ? typeof megaShareTx.amount === "number"
      ? megaShareTx.amount
      : megaShareTx.amount.toNumber?.() ?? 0
    : 0;

  if (claimAmount > 0) {
    return claimAmount / 2;
  }

  return 0;
};
